import { useEffect } from 'react'
import { Pressable, Text, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'

import type { ToastVariant } from '../../contexts/ToastContext'
import { colors } from '../../theme'

interface ToastHostProps {
  toast: {
    id: string
    title: string
    message: string
    variant: ToastVariant
    durationMs: number
    autoDismiss: boolean
    action?: {
      label: string
      onPress: () => void
    }
  } | null
  onDismiss: (id: string) => void
}

function getVariantStyles(variant: ToastVariant): {
  borderColor: string
  titleColor: string
} {
  switch (variant) {
    case 'success':
      return {
        borderColor: colors.coreSuccess,
        titleColor: colors.textHeader,
      }
    case 'error':
      return {
        borderColor: colors.destructive,
        titleColor: colors.textHeader,
      }
    case 'info':
    default:
      return {
        borderColor: colors.border,
        titleColor: colors.textHeader,
      }
  }
}

export function ToastHost({ toast, onDismiss }: ToastHostProps) {
  const { t } = useTranslation('common')
  const insets = useSafeAreaInsets()

  useEffect(() => {
    if (toast == null || !toast.autoDismiss) return

    const timerId = setTimeout(() => {
      onDismiss(toast.id)
    }, toast.durationMs)

    return () => {
      clearTimeout(timerId)
    }
  }, [onDismiss, toast])

  if (toast == null) {
    return null
  }

  const variantStyles = getVariantStyles(toast.variant)

  return (
    <View
      pointerEvents="box-none"
      className="absolute left-0 right-0 z-50 px-4"
      style={{ top: insets.top + 12 }}
    >
      <View
        className="w-full bg-background rounded-xl px-4 py-3 shadow-md"
        style={{ borderColor: variantStyles.borderColor, borderWidth: 1 }}
      >
        <View className="flex-row items-center">
          <View className="flex-1 pr-3">
            <Text
              className="text-base font-semibold"
              style={{ color: variantStyles.titleColor }}
            >
              {toast.title}
            </Text>
            <Text className="text-base mt-1" style={{ color: colors.textBody }}>
              {toast.message}
            </Text>
          </View>

          {toast.action != null ? (
            <Pressable
              onPress={() => {
                toast.action?.onPress()
                onDismiss(toast.id)
              }}
              className="px-2"
              hitSlop={8}
            >
              <Text className="text-base font-semibold text-primary">
                {toast.action.label}
              </Text>
            </Pressable>
          ) : null}

          <Pressable
            onPress={() => onDismiss(toast.id)}
            className="pl-2 py-1"
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={t('toast_dismiss_a11y')}
          >
            <Text className="text-xl leading-5 text-ink">×</Text>
          </Pressable>
        </View>
      </View>
    </View>
  )
}
